import dayjs from "dayjs";
import jsPDF from "jspdf";
import { Entity } from "../../models/viz/Entity";
import { EntityProperties } from "../../models/viz/dataDescriptions/EntityProperties";
import { DateFormats } from "../dates";

const LIST_SEPARATOR = ", " as const;

function adjustWhitespace(s: string): string {
    return s.trim().replace(/\s+/g, " ");
}

function handleDate(d: Date | number): string {
    return dayjs(d).format(DateFormats.yearMonthDayWithTimeSeconds);
}

function handleString(s: string): string {
    return adjustWhitespace(s ?? "");
}

function handleNumber(n: number): string {
    return adjustWhitespace(n?.toString() ?? "");
}

function handleUnknown(u: unknown): string {
    return JSON.stringify(u) ?? "";
}

function convertEntityPropertyToString(prop: { valueType: string; isList: boolean; value: () => unknown }): string {
    switch (prop.valueType) {
        case "date":
            return prop.isList
                ? (prop.value() as Date[]).map(handleDate).join(LIST_SEPARATOR)
                : handleDate(prop.value() as Date);
        case "string":
            return prop.isList
                ? (prop.value() as string[]).map(handleString).join(LIST_SEPARATOR)
                : handleString(prop.value() as string);
        case "number":
            return prop.isList
                ? (prop.value() as number[]).map(handleNumber).join(LIST_SEPARATOR)
                : handleNumber(prop.value() as number);
        default:
            return prop.isList
                ? (prop.value() as unknown[]).map(handleUnknown).join(LIST_SEPARATOR)
                : handleUnknown(prop.value());
    }
}

export interface PdfTableData {
    headers: string[];
    rows: string[][];
    rowEntityIds: Array<string | undefined>;
    rowTypes: Array<"entity" | "comments">;
}

export interface EntityPdfTableOptions {
    titleColumnName: string;
    subtitleColumnName: string;
    ignoreEntityProperties?: string[];
}

export interface CreateEntitiesTablePdfDocumentOptions extends EntityPdfTableOptions {
    entityImageDataUrlByEntityId?: Map<string, string>;
}

export interface SavePdfFileOptions extends CreateEntitiesTablePdfDocumentOptions {
    fileName?: string;
}

export interface CreateRelatedEntitiesReportPdfDocumentOptions {
    primaryOptions: CreateEntitiesTablePdfDocumentOptions;
    secondaryOptions: CreateEntitiesTablePdfDocumentOptions;
    isRelated: (primaryEntity: Entity, secondaryEntity: Entity) => boolean;
    getPrimaryTitle?: (primaryEntity: Entity) => string;
    getPrimarySubtitle?: (primaryEntity: Entity) => string;
    getPrimaryDescription?: (primaryEntity: Entity) => string;
    emptyRelatedMessage?: string;
    reportTitle?: string;
}

const IMAGE_COLUMN_HEADER = "Image" as const;

function isCommentsPropertyKey(key: string): boolean {
    return key === EntityProperties.Comments.key;
}

export function convertEntitiesToPdfTableData(entities: Entity[], options: EntityPdfTableOptions): PdfTableData {
    const ignoredProperties = new Set<string>([
        EntityProperties.Meta.Bounds.key,
        EntityProperties.Meta.BoundsBitmap.key,
        EntityProperties.Meta.LowResBoundsBitmap.key,
        EntityProperties.Meta.FreeFormSearch.key,
        EntityProperties.Meta.CreatedAt.key,
        EntityProperties.Comments.key,
        "FragmentGroupId",
        ...(options.ignoreEntityProperties ?? []),
    ]);

    if (entities.length === 0) {
        return {
            headers: ["ID", IMAGE_COLUMN_HEADER],
            rows: [],
            rowEntityIds: [],
            rowTypes: [],
        };
    }

    const propertyKeys: string[] = [];
    const rows: string[][] = [];
    const rowEntityIds: Array<string | undefined> = [];
    const rowTypes: Array<"entity" | "comments"> = [];

    for (const entity of entities) {
        for (const [key, value] of Object.entries(entity.properties)) {
            if (!propertyKeys.includes(key) && !ignoredProperties.has(key) && value.valueType !== "object") {
                propertyKeys.push(key);

                for (const row of rows) {
                    row.push("");
                }
            }
        }

        const valueByPropertyKey: Record<string, string> = {};

        for (const key of propertyKeys) {
            const prop = entity.properties[key];
            if (prop === undefined) {
                valueByPropertyKey[key] = "";
                continue;
            }

            valueByPropertyKey[key] = convertEntityPropertyToString(prop);
        }

        const row = [entity.id, entity.getTitle() ?? "", entity.getSubtitle() ?? ""];
        for (const key of propertyKeys) {
            row.push(isCommentsPropertyKey(key) ? "" : valueByPropertyKey[key] ?? "");
        }

        row.push("");
        rows.push(row);
        rowEntityIds.push(entity.id);
        rowTypes.push("entity");

        const commentsContent = Object.entries(entity.properties)
            .filter(([key]) => isCommentsPropertyKey(key))
            .map(([, prop]) => convertEntityPropertyToString(prop))
            .find((value) => value.length > 0);

        if (commentsContent !== undefined) {
            const commentsRow = [`Comments for ${entity.getTitle() ?? "above"}: ${commentsContent}`];
            rows.push(commentsRow);
            rowEntityIds.push(undefined);
            rowTypes.push("comments");
        }
    }

    const headers = [
        "ID",
        options.titleColumnName,
        options.subtitleColumnName,
        ...propertyKeys.map((key) => {
            const sourceEntity = entities.find((entity) => entity.properties[key] !== undefined);
            return sourceEntity?.dataDescription.properties[key]?.displayName ?? key;
        }),
        IMAGE_COLUMN_HEADER,
    ];

    return { headers, rows, rowEntityIds, rowTypes };
}

function renderTable(
    doc: jsPDF,
    headers: string[],
    rows: string[][],
    rowImageDataUrls: Array<string | undefined>,
    rowTypes: Array<"entity" | "comments">,
    position: { x?: number; y?: number } = {},
): number {
    const margin = 10;
    const cellPadding = 1.5;
    const imageSize = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableX = position.x ?? margin;
    const tableWidth = pageWidth - tableX * 2;
    const columnWidth = tableWidth / Math.max(headers.length, 1);
    const imageColumnIndex = headers.indexOf(IMAGE_COLUMN_HEADER);
    let y = position.y ?? margin;

    const getLineHeight = (): number => {
        return (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
    };

    const setRowFont = (isHeader: boolean) => {
        doc.setFont("helvetica", isHeader ? "bold" : "normal");
        doc.setFontSize(isHeader ? 9 : 8);
    };

    const drawRow = (cells: string[], isHeader: boolean, rowImageDataUrl?: string, isCommentsRow: boolean = false) => {
        setRowFont(isHeader);

        const lineHeight = getLineHeight();

        if (!isHeader && isCommentsRow) {
            const commentText = cells[0] ?? "";
            const commentLines = doc.splitTextToSize(commentText, Math.max(tableWidth - cellPadding * 2, 2));
            const rowHeight = Math.max(commentLines.length, 1) * lineHeight + cellPadding * 2;

            if (y + rowHeight > pageHeight - margin) {
                doc.addPage();
                y = margin;
                drawRow(headers, true);
                setRowFont(isHeader);
            }

            doc.rect(tableX, y, tableWidth, rowHeight);
            doc.text(
                commentLines.length > 0 ? commentLines : [""],
                tableX + cellPadding,
                y + cellPadding + lineHeight * 0.8,
            );
            y += rowHeight;
            return;
        }

        const getCellLines = (cell: string): string[] => {
            const text = cell ?? "";
            const lines = doc.splitTextToSize(text, Math.max(columnWidth - cellPadding * 2, 2));
            return lines.length > 0 ? lines : [""];
        };

        const rowLines = cells.map(getCellLines);
        const textRowHeight = Math.max(...rowLines.map((lines) => lines.length)) * lineHeight + cellPadding * 2;
        const rowHeight =
            !isHeader && rowImageDataUrl ? Math.max(textRowHeight, imageSize + cellPadding * 2) : textRowHeight;

        if (y + rowHeight > pageHeight - margin) {
            doc.addPage();
            y = margin;
            drawRow(headers, true);
            setRowFont(isHeader);
        }

        let x = tableX;
        for (let index = 0; index < cells.length; index += 1) {
            doc.rect(x, y, columnWidth, rowHeight);

            const isImageCell = !isHeader && index === imageColumnIndex;
            if (isImageCell && rowImageDataUrl) {
                const imageRenderSize = Math.min(imageSize, rowHeight - cellPadding * 2, columnWidth - cellPadding * 2);
                const imageX = x + (columnWidth - imageRenderSize) / 2;
                const imageY = y + (rowHeight - imageRenderSize) / 2;

                try {
                    doc.addImage(rowImageDataUrl, imageX, imageY, imageRenderSize, imageRenderSize);
                } catch {
                    doc.text("[image]", x + cellPadding, y + cellPadding + lineHeight * 0.8);
                }
            } else {
                doc.text(rowLines[index], x + cellPadding, y + cellPadding + lineHeight * 0.8);
            }

            x += columnWidth;
        }

        y += rowHeight;
    };

    drawRow(headers, true);

    for (let index = 0; index < rows.length; index += 1) {
        drawRow(rows[index], false, rowImageDataUrls[index], rowTypes[index] === "comments");
    }

    if (rows.length === 0) {
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("No entity data to export.", tableX, y);
    }

    return y;
}

export function savePdfFile(entities: Entity[], options: SavePdfFileOptions) {
    const doc = createEntitiesTablePdfDocument(entities, options);
    doc.save(options.fileName ?? "report.pdf");
}

export function createEntitiesTablePdfDocument(
    entities: Entity[],
    options: CreateEntitiesTablePdfDocumentOptions,
): jsPDF {
    const { headers, rows, rowEntityIds, rowTypes } = convertEntitiesToPdfTableData(entities, options);
    const orientation = headers.length > 6 ? "landscape" : "portrait";
    const doc = new jsPDF({ orientation });
    const rowImageDataUrls = rowEntityIds.map((entityId) =>
        entityId ? options.entityImageDataUrlByEntityId?.get(entityId) : undefined,
    );
    const margin = 10;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Export", margin, y);
    y += 8;

    renderTable(doc, headers, rows, rowImageDataUrls, rowTypes, { x: margin, y });
    return doc;
}

export function createRelatedEntitiesReportPdfDocument(
    primaryEntities: Entity[],
    secondaryEntities: Entity[],
    options: CreateRelatedEntitiesReportPdfDocumentOptions,
): jsPDF {
    const baseSecondaryTableData = convertEntitiesToPdfTableData(secondaryEntities, options.secondaryOptions);
    const orientation = baseSecondaryTableData.headers.length > 6 ? "landscape" : "portrait";
    const doc = new jsPDF({ orientation });
    const margin = 10;
    const reportTitleOffset = options.reportTitle ? 10 : 0;
    const emptyRelatedMessage = options.emptyRelatedMessage ?? "No related entities found.";

    if (options.reportTitle) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(options.reportTitle, margin, margin);
    }

    if (primaryEntities.length === 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text("No primary entities to export.", margin, margin + reportTitleOffset);
        return doc;
    }

    for (let index = 0; index < primaryEntities.length; index += 1) {
        if (index > 0) {
            doc.addPage();
        }

        const primaryEntity = primaryEntities[index];
        const relatedSecondaryEntities = secondaryEntities.filter((secondaryEntity) =>
            options.isRelated(primaryEntity, secondaryEntity),
        );

        const tableData =
            relatedSecondaryEntities.length > 0
                ? convertEntitiesToPdfTableData(relatedSecondaryEntities, options.secondaryOptions)
                : {
                      headers: baseSecondaryTableData.headers,
                      rows: [[emptyRelatedMessage]],
                      rowEntityIds: [undefined],
                      rowTypes: ["comments" as const],
                  };

        const primaryTitle = options.getPrimaryTitle?.(primaryEntity) ?? primaryEntity.getTitle() ?? primaryEntity.id;
        const primarySubtitle = options.getPrimarySubtitle?.(primaryEntity) ?? primaryEntity.getSubtitle() ?? "";
        const primaryDescription =
            options.getPrimaryDescription?.(primaryEntity) ??
            convertEntityPropertyToString(
                primaryEntity.properties[EntityProperties.Comments.key] ?? {
                    valueType: "string",
                    isList: false,
                    value: () => "",
                },
            );

        let y = index === 0 ? margin + reportTitleOffset : margin;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(primaryTitle, margin, y);
        y += 6;

        if (primarySubtitle.length > 0) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(primarySubtitle, margin, y);
            y += 6;
        }

        if (primaryDescription.length > 0) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(primaryDescription, margin, y);
            y += 8;
        } else {
            y += 2;
        }

        const rowImageDataUrls = tableData.rowEntityIds.map((entityId) =>
            entityId ? options.secondaryOptions.entityImageDataUrlByEntityId?.get(entityId) : undefined,
        );

        renderTable(doc, tableData.headers, tableData.rows, rowImageDataUrls, tableData.rowTypes, { x: margin, y });
    }

    return doc;
}
